import { useState, useEffect, useCallback } from "react";

// Components
import Table from "./Childs/Table";
import Delete from "./Delete";
import Form from "./Childs/Form";
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";
import FormCard from "../../Common/Childs/Structure/FormCard";
import Dropdown from "../../Common/Dropdown";

// Context
import { useGlobal } from "../../../context/GlobalContextProvider";
import { useError } from "../../../context/ErrorContextProvider";

// Assets
import plus from "../../../assets/icons/plus-solid.svg";

// Constants
const INITIAL_FORM_STATE = {
  id: 0,
  layer_name: "",
  layer_depth: "",
  pattern_id: 0,
  pattern_name: "",
};

const DESCRIPTION_TEXT = "Fund Raisin NFTs can have more than one layer. Set up the layers.";

// API Service
const layerAPI = {
  getAll: async (patternId) => {
    const res = await fetch(`/api/pattern/${patternId}/layer`);
    if (res.status === 200) {
      return res.json();
    }
    return [];
  },

  create: async (patternId, data) => {
    return fetch(`/api/pattern/${patternId}/layer`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    });
  },

  update: async (patternId, id, data) => {
    return fetch(`/api/pattern/${patternId}/layer/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    });
  },

  delete: async (patternId, id) => {
    const res = await fetch(`/api/pattern/${patternId}/layer/${id}`, {
      method: "DELETE",
      headers: { "Content-type": "application/json; charset=UTF-8" },
    });
    if (res.status === 204 || !res.ok) return null;
    return res.json();
  },
};

const patternAPI = {
  getAll: async (artPackId) => {
    const res = await fetch(`/api/artpack/${artPackId}/pattern`);
    if (res.status === 200) {
      return res.json();
    }
    return [];
  },
};


const Index = () => {
  const { artPack, setSpinner } = useGlobal();
  const { showError } = useError();

  const [layers, setLayers] = useState([]);
  const [patternList, setPatternList] = useState([]);
  const [selectedPatternId, setSelectedPatternId] = useState(0);
  const [selectedPatternName, setSelectedPatternName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isDeleteMode, setIsDeleteMode] = useState(false);

  // Normalize layer data
  const normalizeLayers = useCallback((items) => {
    return items.map((el) => ({
      layer_id: el.layer_id,
      layer_name: el.layer_name,
      layer_depth: el.layer_depth,
      pattern_id: el.pattern_id,
    }));
  }, []);

  // Normalize pattern data
  const normalizePatterns = useCallback((items) => {
    return items.map((el) => ({
      id: el.pattern_id,
      name: el.pattern_name,
    }));
  }, []);

  // Load patterns when art pack changes
  const loadPatterns = useCallback(async () => {
    if (!artPack?.art_pack_id) return;

    try {
      const data = await patternAPI.getAll(artPack.art_pack_id);
      const normalizedPatterns = normalizePatterns(data);

      if (normalizedPatterns.length > 0) {
        setPatternList(normalizedPatterns);
        setSelectedPatternId(normalizedPatterns[0].id);
        setSelectedPatternName(normalizedPatterns[0].name);
      } else {
        setPatternList([]);
        setSelectedPatternId(0);
        setSelectedPatternName("");
      }
    } catch (error) {
      console.error("Error loading patterns:", error);
    }
  }, [artPack?.art_pack_id, normalizePatterns]);

  // Load layers for selected pattern
  const loadLayers = useCallback(async () => {
    if (!selectedPatternId) {
      setLayers([]);
      return;
    }

    setSpinner(true);
    try {
      const data = await layerAPI.getAll(selectedPatternId);
      setLayers(normalizeLayers(data));
    } catch (error) {
      console.error("Error loading layers:", error);
    } finally {
      setSpinner(false);
    }
  }, [selectedPatternId, setSpinner, normalizeLayers]);

  // Load patterns on art pack change
  useEffect(() => {
    loadPatterns();
  }, [loadPatterns]);

  // Load layers when pattern changes
  useEffect(() => {
    loadLayers();
  }, [loadLayers]);

  // Reset form and close
  const resetForm = useCallback(() => {
    setShowForm(false);
    setIsDeleteMode(false);
    setFormData(INITIAL_FORM_STATE);
  }, []);

  // Open form for new layer
  const handleAddNew = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setShowForm(true);
    setIsDeleteMode(false);
  }, []);

  // Open form for editing
  const handleEdit = useCallback((data) => {
    setFormData(data);
    setShowForm(true);
    setIsDeleteMode(false);
  }, []);

  // Open delete confirmation
  const handleDelete = useCallback((data) => {
    setFormData(data);
    setShowForm(false);
    setIsDeleteMode(true);
  }, []);

  // Handle pattern selection
  const handlePatternChange = useCallback((id) => {
    setSelectedPatternId(id);
    const selectedPattern = patternList.find((p) => p.id === id);
    setSelectedPatternName(selectedPattern ? selectedPattern.name : "Choose an option");
  }, [patternList]);

  // Create new layer
  const handleCreate = useCallback(async (data) => {
    setSpinner(true);
    try {
      await layerAPI.create(selectedPatternId, data);
      await loadLayers();
      resetForm();
    } catch (error) {
      console.error("Error creating layer:", error);
    } finally {
      setSpinner(false);
    }
  }, [selectedPatternId, loadLayers, resetForm, setSpinner]);

  // Update existing layer
  const handleUpdate = useCallback(async (data) => {
    setSpinner(true);
    try {
      await layerAPI.update(selectedPatternId, formData.id, data);
      await loadLayers();
      resetForm();
    } catch (error) {
      console.error("Error updating layer:", error);
    } finally {
      setSpinner(false);
    }
  }, [selectedPatternId, formData.id, loadLayers, resetForm, setSpinner]);

  // Delete layer
  const handleDeleteConfirm = useCallback(async () => {
    setSpinner(true);
    try {
      const result = await layerAPI.delete(selectedPatternId, formData.id);
      if (result.message) {
        showError(result.message);
      }
      await loadLayers();
      resetForm();
    } catch (error) {
      console.error("Error deleting layer:", error);
    } finally {
      setSpinner(false);
    }
  }, [selectedPatternId, formData.id, loadLayers, resetForm, setSpinner, showError]);

  // Render delete confirmation
  if (isDeleteMode && formData.id) {
    return (
      <Delete
        onDiscard={resetForm}
        handleSubmit={handleDeleteConfirm}
        formData={formData}
      />
    );
  }

  // Render form
  if (showForm) {
    const isEditMode = Boolean(formData.layer_name);

    return (
      <>
        <Title title="Layers" />
        <Description text={DESCRIPTION_TEXT} />
        <FormCard>
          <Form
            handleSubmit={isEditMode ? handleUpdate : handleCreate}
            formData={formData}
            formDataPattern={patternList}
            onDiscard={resetForm}
            typeCheck={isEditMode ? 'updateItem' : 'createItem'}
            baseImageId={selectedPatternId}
          />
        </FormCard>
      </>
    );
  }

  // Render main list view
  return (
    <>
      <Title title="Layers" />
      <Description text={DESCRIPTION_TEXT} />
      
      {patternList.length > 0 && (
        <div style={{ width: '30%', marginBottom: '2rem' }}>
          <Dropdown
            category="artist"
            categorySubType="layer"
            dropdownList={patternList}
            dropdownHeader={selectedPatternName}
            handleChange={handlePatternChange}
          />
        </div>
      )}

      {!artPack?.approved && selectedPatternId > 0 && (
        <div className="add-btn" onClick={handleAddNew}>
          Add Layer
          <img src={plus} className="add-icon" alt="Add" />
        </div>
      )}

      <Table
        data={layers}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isAllowedToEditOrDelete={!artPack?.approved}
      />
    </>
  );
};

export default Index;
